import { useEffect, useRef, useState } from "react";
import { Form, Formik } from "formik";
import { useTranslation } from "react-i18next";
import { Tooltip } from "react-tooltip";
import classNames from "classnames";
import { format } from "date-fns";
import style from "../EventsAudit/EventAuditList.module.css";

import { useAppSelector } from "../../shared/hook";
import Pagination from "../../shared/components/Table/Pagination";
import { EmptyListPlaceholderContent } from "../../shared/Ui/EmptyPageBlock";
import Calendar from "../../shared/Ui/Calendar/Calendar";
import PageNotRole from "../../shared/Ui/PageNotFound/PageNotRole";
import ActionDropwdownCell from "../../shared/components/Table/ActionDropwdownCell";
import {
  exportReconciliationReport,
  getReconciliationReport,
  ReconciliationReportRow,
} from "../../common/api/billingApi";
import {
  canAccessLocalBilling,
  downloadXlsxBlob,
  formatBillingAmount,
} from "./billingUtils";

type PaginationState = {
  pageIndex: number;
  pageSize: number;
};

const formatCurrency = formatBillingAmount;

const downloadBlob = downloadXlsxBlob;

function ReconciliationReportPage() {
  const { t } = useTranslation();
  const userData: any = useAppSelector((state) => state.auth.userData);
  const pageSize = useAppSelector((state) => state.userSettings.pageSize);
  const exportMenuRef = useRef<HTMLDivElement>(null);

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: pageSize,
  });
  const [rows, setRows] = useState<ReconciliationReportRow[]>([]);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isReportGenerated, setIsReportGenerated] = useState(false);
  const [activeTooltipId, setActiveTooltipId] = useState<number | null>(null);
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [appliedPeriod, setAppliedPeriod] = useState<{
    dateFrom: string;
    dateTo: string;
  } | null>(null);
  const [totals, setTotals] = useState({
    totalMarksCount: 0,
    totalTopUpAmount: 0,
    totalWriteOffAmount: 0,
    totalBalanceOut: 0,
  });

  const hasLocalBillingAccess = canAccessLocalBilling(userData);

  const formatApiDate = (date?: Date) =>
    date ? format(date, "yyyy-MM-dd") : "";

  const paginatedRows = rows.slice(
    pagination.pageIndex * pagination.pageSize,
    (pagination.pageIndex + 1) * pagination.pageSize,
  );
  const totalPages = Math.ceil(rows.length / pagination.pageSize) || 0;

  const loadReport = async (dateFrom: string, dateTo: string) => {
    try {
      const response = await getReconciliationReport(dateFrom, dateTo);
      setRows(response?.rows ?? []);
      setTotals({
        totalMarksCount: response?.totalMarksCount ?? 0,
        totalTopUpAmount: response?.totalTopUpAmount ?? 0,
        totalWriteOffAmount: response?.totalWriteOffAmount ?? 0,
        totalBalanceOut: response?.totalBalanceOut ?? 0,
      });
      setIsReportGenerated(true);
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    } catch (error) {
      console.error("Error fetching reconciliation report:", error);
      setRows([]);
      setIsReportGenerated(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        exportMenuRef.current &&
        !exportMenuRef.current.contains(event.target as Node)
      ) {
        setIsExportOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleExportExcel = async () => {
    if (!appliedPeriod) return;
    setIsExporting(true);
    try {
      const blob = await exportReconciliationReport(
        appliedPeriod.dateFrom,
        appliedPeriod.dateTo,
      );
      downloadBlob(
        blob,
        `Акт сверки ${appliedPeriod.dateFrom} - ${appliedPeriod.dateTo}.xlsx`,
      );
    } catch (error) {
      console.error("Error exporting reconciliation report:", error);
      window.alert(t("billing.exportError"));
    } finally {
      setIsExporting(false);
      setIsExportOpen(false);
    }
  };

  const renderFilterInputs = () => (
    <Formik
      initialValues={{ dateFrom: "", dateTo: "" }}
      onSubmit={() => {
        if (!startDate || !endDate) return;
        const dateFrom = formatApiDate(startDate);
        const dateTo = formatApiDate(endDate);
        setAppliedPeriod({ dateFrom, dateTo });
        setIsFilterOpen(false);
        setIsReportGenerated(false);
        loadReport(dateFrom, dateTo);
      }}
    >
      {() => (
        <Form className="max-w-[1585px] w-full">
          <div className="flex flex-row align-center justify-between">
            <p className="my-3 text-lg font-semibold">
              {t("documents_upload.filters")}
            </p>
            <button type="button" onClick={() => setIsFilterOpen(false)}>
              <img src="/icons/close.svg" alt="close" />
            </button>
          </div>
          <div className="flex flex-col">
            <div className={style.inputField}>
              <div className={style.labelAndButtonWrapper}>
                <label className={style.inputLabel}>{t("billing.dateFrom")}</label>
              </div>
              <Calendar
                startDate={startDate}
                endDate={endDate}
                setStartDate={setStartDate}
                setEndDate={setEndDate}
                showMonthYearSelect
              />
            </div>
          </div>
          <div className="flex flex-row gap-[10px] justify-end mt-2">
            <button
              type="submit"
              className="bg-green text-white font-semibold py-2 px-4 rounded"
              disabled={!startDate || !endDate}
            >
              {t("mainButtons3.apply")}
            </button>
            <button
              type="button"
              className="bg-bg text-gray-500 font-semibold py-2 px-4 rounded"
              onClick={() => {
                setStartDate(undefined);
                setEndDate(undefined);
                setAppliedPeriod(null);
                setRows([]);
                setIsReportGenerated(false);
                setPagination((prev) => ({ ...prev, pageIndex: 0 }));
              }}
            >
              {t("mainButtons3.reset")}
            </button>
          </div>
        </Form>
      )}
    </Formik>
  );

  if (!hasLocalBillingAccess) {
    return <PageNotRole />;
  }

  return (
    <div className="flex flex-1 flex-col min-w-0">
      <h2 className="my-3 text-xl font-semibold">{t("billing.periodReportTitle")}</h2>
      <div className="bg-white my-5 p-5 pt-8 relative rounded-xl shadow-md flex flex-row items-center justify-between">
        <div className="flex flex-row gap-[10px] items-center">
          {isReportGenerated && appliedPeriod && (
            <div className="relative" ref={exportMenuRef}>
              <button
                type="button"
                className="bg-green text-white font-semibold py-2 px-4 rounded disabled:opacity-60"
                disabled={isExporting}
                onClick={() => setIsExportOpen((prev) => !prev)}
              >
                {t("mainButtons.download")}
              </button>
              {isExportOpen && (
                <div className="absolute left-0 mt-2 min-w-[140px] bg-white rounded-lg shadow-lg z-50 overflow-hidden">
                  <button
                    type="button"
                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100"
                    onClick={handleExportExcel}
                  >
                    Excel
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="flex flex-row gap-[10px] items-center">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setIsFilterOpen(true);
            }}
            className="bg-green text-white font-semibold py-2 px-4 rounded"
            data-tooltip-offset={4}
            data-tooltip-id="billing-reconciliation-filter"
          >
            {t("mainButtons3.filter")}
          </button>
          <button
            type="button"
            onClick={() => {
              if (appliedPeriod) {
                loadReport(appliedPeriod.dateFrom, appliedPeriod.dateTo);
              }
            }}
          >
            <img src="/icons/update.svg" alt="update" />
          </button>
        </div>
      </div>
      <div className="w-full">
        <div className="rounded-lg overflow-x-auto bg-white">
          <table className="max-w-full w-full leading-normal">
            <thead className="border-b-2 border-gray-300">
              <tr>
                <th className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200">
                  №
                </th>
                <th className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200">
                  {t("billing.orgIinBin")}
                </th>
                <th className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200">
                  {t("billing.orgName")}
                </th>
                <th className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200">
                  {t("billing.accountNumber")}
                </th>
                <th className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200">
                  {t("billing.productGroup")}
                </th>
                <th className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200">
                  {t("billing.marksCount")}
                </th>
                <th className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200">
                  {t("billing.topUpAmount")}
                </th>
                <th className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200">
                  {t("billing.writeOffAmount")}
                </th>
                <th className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200" />
              </tr>
            </thead>
            {!isReportGenerated ? (
              <tbody>
                <tr>
                  <td colSpan={9} className="text-center py-5">
                    <EmptyListPlaceholderContent
                      title={t("billing.selectPeriodToGenerate")}
                    />
                  </td>
                </tr>
              </tbody>
            ) : paginatedRows.length === 0 ? (
              <tbody>
                <tr>
                  <td colSpan={9} className="text-center py-5">
                    <EmptyListPlaceholderContent title={t("billing.noData")} />
                  </td>
                </tr>
              </tbody>
            ) : (
              <tbody className="font-normal text-center">
                {paginatedRows.map((row, index) => (
                  <tr key={`reconciliation-${row.accountId}-${index}`}>
                    <td className="px-5 py-5 text-sm border-b border-gray-200 text-gray-600">
                      {pagination.pageIndex * pagination.pageSize + index + 1}
                    </td>
                    <td className="px-5 py-5 text-sm border-b border-gray-200 text-gray-600">
                      {row.orgIinBin ?? "-"}
                    </td>
                    <td className="px-5 py-5 text-sm border-b border-gray-200 text-gray-600">
                      {row.orgName ?? "-"}
                    </td>
                    <td className="px-5 py-5 text-sm border-b border-gray-200 text-gray-600">
                      {row.accountNumber ?? "-"}
                    </td>
                    <td className="px-5 py-5 text-sm border-b border-gray-200 text-gray-600">
                      {row.productGroupName ?? row.productGroupCode ?? "-"}
                    </td>
                    <td className="px-5 py-5 text-sm border-b border-gray-200 text-gray-600">
                      {row.marksCount ?? "-"}
                    </td>
                    <td className="px-5 py-5 text-sm border-b border-gray-200 text-gray-600">
                      {formatCurrency(row.topUpAmount)}
                    </td>
                    <td className="px-5 py-5 text-sm border-b border-gray-200 text-gray-600">
                      {formatCurrency(row.writeOffAmount)}
                    </td>
                    <td className="px-5 py-5 text-sm border-b border-gray-200 text-gray-600">
                      <ActionDropwdownCell
                        id={row.accountId ?? index}
                        actions={[]}
                        activeTooltipId={activeTooltipId}
                        onTooltipToggle={(id) =>
                          setActiveTooltipId((prev) => (prev === id ? null : id))
                        }
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>
        {isReportGenerated && rows.length > 0 && (
          <>
            <div className="bg-white px-5 py-3 text-sm text-gray-600 border-t border-gray-200 flex flex-wrap gap-6">
              <span>
                {t("billing.totalMarksCount")}: {totals.totalMarksCount}
              </span>
              <span>
                {t("billing.totalTopUp")}: {formatCurrency(totals.totalTopUpAmount)}
              </span>
              <span>
                {t("billing.totalWriteOff")}:{" "}
                {formatCurrency(totals.totalWriteOffAmount)}
              </span>
              <span>
                {t("billing.totalBalanceOut")}:{" "}
                {formatCurrency(totals.totalBalanceOut)}
              </span>
            </div>
            <Pagination
              totalItems={rows.length}
              totalPages={totalPages}
              pagination={pagination}
              setPagination={setPagination}
            />
          </>
        )}
      </div>
      <div className="z-[1000] relative">
        <Tooltip
          isOpen={isFilterOpen}
          id="billing-reconciliation-filter"
          className={classNames(style.wrapperTooltip, "drop-shadow-2xl")}
          openOnClick
          clickable
          noArrow
          opacity={1}
          place="bottom-end"
          afterShow={() => setIsFilterOpen(true)}
          afterHide={() => setIsFilterOpen(false)}
        >
          <div onClick={(e) => e.stopPropagation()}>{renderFilterInputs()}</div>
        </Tooltip>
      </div>
    </div>
  );
}

export default ReconciliationReportPage;
